import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Calendar, AlertTriangle, TrendingDown, UserX, Clock } from 'lucide-react';

const ProblemSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const timeline = [
    {
      day: 'Día 1-3',
      icon: Calendar,
      title: 'Motivación alta',
      description: 'Tu paciente sale de consulta comprometido con el plan.',
      status: 'good',
    },
    {
      day: 'Día 7-10',
      icon: AlertTriangle,
      title: 'Primeras dudas',
      description: '"¿Puedo comer esto?" No hay quien responda. Improvisan.',
      status: 'warning',
    },
    {
      day: 'Día 15-20',
      icon: TrendingDown,
      title: 'Deslices frecuentes',
      description: 'Sin accountability, los pequeños errores se acumulan.',
      status: 'danger',
    },
    {
      day: 'Día 25-29',
      icon: UserX,
      title: 'Abandono silencioso',
      description: '70% no regresa. Culpa, vergüenza, y a buscar otro nutricionista.',
      status: 'critical',
    },
  ];

  const statusColors = {
    good: 'bg-[#2E5C41] border-[#2E5C41]',
    warning: 'bg-amber-500 border-amber-500',
    danger: 'bg-orange-500 border-orange-500',
    critical: 'bg-[#D95A39] border-[#D95A39]',
  };

  return (
    <section ref={ref} className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-white" data-testid="problem-section">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center max-w-3xl mx-auto mb-12 md:mb-16"
        >
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#849B89] mb-4">
            El problema real
          </span>
          <h2 className="font-cabinet text-3xl md:text-4xl font-bold text-[#1A2E24] tracking-tight mb-4">
            Los 29 días que destruyen
            <span className="text-[#D95A39]"> el 70% de tus resultados</span>
          </h2>
          <p className="text-base md:text-lg text-[#4B5E53] leading-relaxed">
            No es tu plan lo que falla. Es la brecha entre consultas donde tus pacientes están solos, sin guía ni apoyo.
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          {/* Connection line */}
          <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#2E5C41] via-amber-500 via-orange-500 to-[#D95A39] hidden md:block" />

          <div className="space-y-8 md:space-y-0">
            {timeline.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className={`md:flex items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
              >
                {/* Content */}
                <div className={`md:w-5/12 ${index % 2 === 0 ? 'md:text-right md:pr-12' : 'md:text-left md:pl-12'}`}>
                  <div className={`p-6 bg-[#FAFAF9] rounded-2xl border border-[#2E5C41]/10 hover-lift ${index % 2 === 0 ? 'md:ml-auto' : 'md:mr-auto'} max-w-md`}>
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold text-white mb-3 ${statusColors[item.status].split(' ')[0]}`}>
                      {item.day}
                    </span>
                    <h3 className="font-cabinet text-xl font-bold text-[#1A2E24] mb-2">{item.title}</h3>
                    <p className="text-sm text-[#4B5E53]">{item.description}</p>
                  </div>
                </div>

                {/* Icon node */}
                <div className="hidden md:flex w-2/12 justify-center relative z-10">
                  <div className={`w-14 h-14 rounded-full ${statusColors[item.status]} flex items-center justify-center shadow-lg`}>
                    <item.icon className="w-6 h-6 text-white" strokeWidth={1.5} />
                  </div>
                </div>

                {/* Spacer */}
                <div className="md:w-5/12" />
              </motion.div>
            ))}
          </div>
        </div>

        {/* Bottom message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.8 }}
          className="mt-12 md:mt-16 text-center"
        >
          <div className="inline-flex items-center gap-3 px-6 py-4 bg-[#D95A39]/10 rounded-2xl border border-[#D95A39]/20">
            <Clock className="w-6 h-6 text-[#D95A39]" strokeWidth={1.5} />
            <p className="text-[#1A2E24] font-medium">
              <span className="font-bold">El problema no es el plan.</span> Es que no estás ahí cuando más te necesitan.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProblemSection;
